/**
 * The Director's side of the page: whether it is offered here, and a run of
 * it as a stream of AssistEvents.
 *
 * `POST /v1/assist` answers `text/event-stream`; each SSE `data` is one
 * AssistEvent as JSON, checked here field by field before anything trusts
 * it. The Director calls the API on the server, with the viewer's key, so
 * those calls never pass through this page's Client; each `call` and
 * `result` event is turned into a CallRecord and injected, and the Wire,
 * the recorder and the reconciler hear them like any call made by hand.
 *
 * Problems before the stream (400, 402, 403, 429, 503) come back as one
 * `problem` event. Stop aborts at once; the run ends with a `stop` of reason
 * `aborted` built from what the stream had said so far.
 *
 * The user's own Anthropic key, when they entered one, rides in the run's
 * body as `anthropicKey` and nowhere else; without one the field is absent
 * and the deployment's key is spent. A key is checked through the Client
 * (`assistKeyCheck`), so the Wire shows the check, with the key redacted.
 */

import type {
  Args, AssistConfig, AssistEvent, AssistKeyCheck, AssistKeys, AssistKeySource, AssistLimits, AssistMonth, AssistRequest, CallRecord, CreateDirector,
  DirectorClient, Json, SessionApi, Client, Twins,
} from '../contracts';

import { curlOf, keyHint, redact, redactText } from '../spec/twins';
import { readSse } from './sse';
import { isObj, type JsonObject } from '../model/json';

export interface DirectorDeps {
  readonly session: SessionApi;
  readonly client: Client;
  readonly fetchFn?: typeof fetch;
  readonly now?: () => number;
}

const s = (v: unknown): v is string => typeof v === 'string';
const n = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const month = (v: unknown): v is { period?: unknown; used: number; limit: number } => isObj(v) && n(v.used) && n(v.limit);
/** The month as the spec names it; a server that left `period` out still reads. */
const monthOf = (v: { period?: unknown; used: number; limit: number }): AssistMonth => ({ period: s(v.period) ? v.period : '', used: v.used, limit: v.limit });
const limits = (v: unknown): v is AssistLimits =>
  isObj(v) && ['turns', 'toolCalls', 'outputTokens', 'inputTokens', 'wallSeconds', 'creates'].every((k) => n(v[k]));

/** Whose Anthropic key a run spends; a server that predates it says nothing, and nothing is assumed. */
const keySource = (v: unknown): v is AssistKeySource => v === 'own' || v === 'deployment' || v === 'scripted';
const keysOf = (v: unknown): AssistKeys | null =>
  isObj(v) && typeof v.deployment === 'boolean' && typeof v.own === 'boolean' ? { deployment: v.deployment, own: v.own } : null;

const WHY = new Set(['not-offered', 'outside-scope', 'look-mode', 'create-cap', 'budget', 'invalid-arguments']);
const REASONS = new Set(['end_turn', 'max_turns', 'max_calls', 'budget', 'deadline', 'refusal', 'max_tokens', 'aborted', 'error']);

const problemOf = (v: unknown): { type: string; title: string; status: number; detail: string } | null =>
  isObj(v) && s(v.title) && n(v.status) && s(v.detail) ? { type: s(v.type) ? v.type : 'about:blank', title: v.title, status: v.status, detail: v.detail } : null;

/** An AssistEvent, if the JSON is one; null for anything else, which is then ignored. */
export const readAssistEvent = (v: unknown): AssistEvent | null => {
  if (!isObj(v) || !s(v.type)) return null;
  switch (v.type) {
    case 'open':
      return s(v.assistId) && s(v.model) && (v.mode === 'act' || v.mode === 'look') && Array.isArray(v.tools) && v.tools.every(s) && limits(v.limits) && month(v.month)
        ? {
          type: 'open', assistId: v.assistId, model: v.model, mode: v.mode, ...(keySource(v.key) ? { key: v.key } : {}),
          tools: v.tools as string[], limits: v.limits, month: monthOf(v.month),
        }
        : null;
    case 'note':
    case 'text':
      return s(v.delta) ? { type: v.type, delta: v.delta } : null;
    case 'forming':
      return s(v.callId) && s(v.tool) && s(v.partial) ? { type: 'forming', callId: v.callId, tool: v.tool, partial: v.partial } : null;
    case 'call':
      return n(v.step) && s(v.callId) && s(v.tool) && isObj(v.arguments) && isObj(v.rest) && s(v.rest.method) && s(v.rest.path) && (v.rest.query === undefined || s(v.rest.query))
        ? {
          type: 'call', step: v.step, callId: v.callId, tool: v.tool, arguments: v.arguments,
          rest: { method: v.rest.method, path: v.rest.path, ...(s(v.rest.query) ? { query: v.rest.query } : {}) }, mcp: v.mcp ?? null,
        }
        : null;
    case 'result':
      return n(v.step) && s(v.callId) && s(v.tool) && n(v.status) && typeof v.truncated === 'boolean' && (v.head === null || s(v.head)) && (v.commitSeq === null || n(v.commitSeq)) && n(v.ms)
        ? { type: 'result', step: v.step, callId: v.callId, tool: v.tool, status: v.status, body: v.body ?? null, truncated: v.truncated, head: v.head, commitSeq: v.commitSeq, ms: v.ms }
        : null;
    case 'denied':
      return n(v.step) && s(v.callId) && s(v.tool) && s(v.why) && WHY.has(v.why) && s(v.detail)
        ? { type: 'denied', step: v.step, callId: v.callId, tool: v.tool, why: v.why as Extract<AssistEvent, { type: 'denied' }>['why'], detail: v.detail }
        : null;
    case 'usage':
      return n(v.turn) && n(v.input) && n(v.output) && n(v.cacheRead) && n(v.cacheWrite)
        ? { type: 'usage', turn: v.turn, input: v.input, output: v.output, cacheRead: v.cacheRead, cacheWrite: v.cacheWrite }
        : null;
    case 'stop': {
      if (!(s(v.reason) && REASONS.has(v.reason) && n(v.turns) && n(v.calls) && month(v.month))) return null;
      const refusal = isObj(v.refusal)
        ? { category: s(v.refusal.category) ? v.refusal.category : null, explanation: s(v.refusal.explanation) ? v.refusal.explanation : null }
        : undefined;
      return {
        type: 'stop', reason: v.reason as Extract<AssistEvent, { type: 'stop' }>['reason'], turns: v.turns, calls: v.calls,
        ...(refusal ? { refusal } : {}), month: monthOf(v.month),
      };
    }
    case 'problem': {
      const p = problemOf(v.problem);
      return p ? { type: 'problem', problem: p } : null;
    }
    default:
      return null;
  }
};

/** An AssistConfig, if the JSON is one. */
export const readAssistConfig = (v: unknown): AssistConfig | null => {
  if (!isObj(v) || typeof v.available !== 'boolean' || !limits(v.limits) || !isObj(v.tools) || !isObj(v.month)) return null;
  const list = (x: unknown): string[] => (Array.isArray(x) ? x.filter(s) : []);
  const keys = keysOf(v.keys);
  return {
    available: v.available,
    reason: s(v.reason) ? v.reason : null,
    model: s(v.model) ? v.model : null,
    modes: list(v.modes).filter((m): m is 'look' | 'act' => m === 'look' || m === 'act'),
    tools: { look: list(v.tools.look), act: list(v.tools.act) },
    limits: v.limits,
    month: { period: s(v.month.period) ? v.month.period : '', used: n(v.month.used) ? v.month.used : 0, limit: n(v.month.limit) ? v.month.limit : 0 },
    disclosure: s(v.disclosure) ? v.disclosure : '',
    ...(keys ? { keys } : {}),
  };
};

const unreachable = (base: string, error: unknown) =>
  ({ type: 'about:blank', title: 'Unreachable', status: 0, detail: `could not reach ${base} (${redactText(String(error))})` });

/** The twins of a call the Director made, from what the server said it would call. */
const twinsOfCall = (base: string, key: string | null, ev: Extract<AssistEvent, { type: 'call' }>): Twins => {
  const query = ev.rest.query ? `?${ev.rest.query.replace(/^\?/, '')}` : '';
  const target = `${ev.rest.path}${query}`;
  const body = ev.arguments.body;
  const headers: Record<string, string> = {};
  if (body !== undefined && ev.rest.method !== 'GET') headers['content-type'] = 'application/json';
  const idem = ev.arguments['Idempotency-Key'];
  if (typeof idem === 'string') headers['Idempotency-Key'] = idem;
  return {
    rest: {
      method: ev.rest.method, url: redactText(`${base}${target}`),
      headers: { ...(key ? { authorization: `Bearer ${keyHint(key)}` } : {}), ...headers },
      ...(body === undefined || ev.rest.method === 'GET' ? {} : { body: redact(body) }),
    },
    curl: curlOf(ev.rest.method, target, headers, ev.rest.method === 'GET' ? undefined : body, '$SB_KEY'),
    mcp: redact(ev.mcp),
  };
};

export const createDirector: CreateDirector = (deps) => makeDirector(deps);

export function makeDirector(deps: DirectorDeps): DirectorClient {
  const fetchFn = deps.fetchFn ?? ((input, init) => fetch(input, init));
  const now = deps.now ?? Date.now;
  const base = () => deps.session.apiBase;
  /** What the deployment last said of the Director: whether the user's key would be spent there at all. */
  let last: AssistConfig | null = null;
  const auth = (): Record<string, string> => {
    const key = deps.session.key('A');
    return key ? { authorization: `Bearer ${key}` } : {};
  };

  return {
    async config() {
      let response: Response;
      try {
        response = await fetchFn(`${base()}/v1/assist/config`, { headers: auth() });
      } catch (error) {
        return { problem: unreachable(base(), error) };
      }
      const text = await response.text().catch(() => '');
      let body: Json = null;
      try { body = text ? JSON.parse(text) as Json : null; } catch { body = text; }
      const config = response.ok ? readAssistConfig(body) : null;
      if (config) return (last = config);
      return { problem: isObj(body) ? body : { type: 'about:blank', title: response.statusText || 'Error', status: response.status, detail: typeof body === 'string' && body ? body : `the API answered ${response.status}` } };
    },

    run(req: AssistRequest, signal: AbortSignal): AsyncIterable<AssistEvent> {
      // The user's own key only when they entered one (for this run, or for the tab): absent, the deployment's is spent.
      // The tab's key stays home where the deployment said it would not be spent: a script plays there
      // whatever key comes, or own keys are not taken (an older server refuses the field).
      const spendable = last === null || (last.model !== 'scripted' && last.keys?.own === true);
      const own = req.anthropicKey || (spendable ? deps.session.anthropicKey() : null);
      const { anthropicKey: _dropped, ...rest } = req;
      return runAssist(own ? { ...rest, anthropicKey: own } : rest, signal);
    },

    async checkKey(key: string): Promise<AssistKeyCheck | { problem: Json }> {
      const record = await deps.client.call('assistKeyCheck', { body: { anthropicKey: key } }, { source: { kind: 'hand', via: 'click' } });
      const a = record.answer;
      const b = a?.body;
      if (a && a.status === 200 && isObj(b) && typeof b.valid === 'boolean' && s(b.detail)) return { valid: b.valid, detail: redactText(b.detail) };
      return { problem: isObj(b) ? redact(b) : { type: 'about:blank', title: 'Error', status: a?.status ?? 0, detail: `the API answered ${a?.status ?? 0}` } };
    },
  };

  async function* runAssist(req: AssistRequest, signal: AbortSignal): AsyncGenerator<AssistEvent> {
    let response: Response;
    try {
      response = await fetchFn(`${base()}/v1/assist`, {
        method: 'POST', headers: { 'content-type': 'application/json', accept: 'text/event-stream', ...auth() },
        body: JSON.stringify(req), signal,
      });
    } catch (error) {
      if (signal.aborted) return;
      yield { type: 'problem', problem: unreachable(base(), error) };
      return;
    }
    const media = response.headers.get('content-type') ?? '';
    if (!response.ok || !media.startsWith('text/event-stream') || !response.body) {
      const text = await response.text().catch(() => '');
      let body: unknown = null;
      try { body = text ? JSON.parse(text) : null; } catch { body = null; }
      yield {
        type: 'problem',
        problem: problemOf(body) ?? { type: 'about:blank', title: response.statusText || 'Error', status: response.status, detail: text || `the API answered ${response.status} without an event stream` },
      };
      return;
    }

    const key = deps.session.key('A');
    const records = new Map<string, CallRecord>();
    let opened: Extract<AssistEvent, { type: 'open' }> | null = null;
    let turns = 0;
    let calls = 0;
    let stopped = false;

    for await (const message of readSse(response.body, signal)) {
      let json: unknown;
      try { json = JSON.parse(message.data); } catch { continue; }
      const ev = readAssistEvent(json);
      if (!ev) continue;
      if (ev.type === 'open') opened = ev;
      if (ev.type === 'usage') turns = Math.max(turns, ev.turn);
      if (ev.type === 'call') {
        calls++;
        const record: CallRecord = {
          id: `${opened?.assistId ?? 'assist'}-${ev.callId}`, op: ev.tool, args: ev.arguments as Args, persona: 'A',
          source: { kind: 'director', assistId: opened?.assistId ?? '', step: ev.step, callId: ev.callId },
          origin: null, via: 'mcp', twins: twinsOfCall(base(), key, ev), sentAt: now(), answer: null,
        };
        records.set(ev.callId, record);
        deps.client.inject(record, 'sent');
      }
      if (ev.type === 'result') {
        const record = records.get(ev.callId);
        if (record) {
          records.delete(ev.callId);
          record.answer = { status: ev.status, body: ev.body, head: ev.head, ms: ev.ms, requestId: null, rate: null };
          deps.client.inject(record, 'answered');
        }
      }
      if (ev.type === 'stop') stopped = true;
      yield ev;
      if (stopped) break;
    }

    // Calls that never heard back: say so, so no call waits on the Wire forever.
    for (const record of records.values()) {
      record.answer = {
        status: 0, body: { type: 'about:blank', title: 'No Answer', status: 0, detail: 'the Director stopped before this call answered; if it was made, the journal has it' } as JsonObject,
        head: null, ms: now() - record.sentAt, requestId: null, rate: null,
      };
      deps.client.inject(record, 'answered');
    }
    if (stopped || !opened) return;
    if (signal.aborted) {
      yield { type: 'stop', reason: 'aborted', turns, calls, month: opened.month };
      return;
    }
    yield { type: 'problem', problem: { type: 'about:blank', title: 'Stream Lost', status: 0, detail: 'the Director\'s stream ended before it said stop; calls already made stand' } };
    yield { type: 'stop', reason: 'error', turns, calls, month: opened.month };
  }
}
